import React, { useState, useEffect } from 'react';
import { Shield, Heart, Users, Award, CheckCircle, ArrowRight, Calendar, Target, BookOpen, Briefcase, Phone, Mail, MapPin, Eye } from 'lucide-react';

const ProgramsPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const programs = [
    {
    id: 'infectious-disease-control',
    icon: Shield,
    title: "Infectious Disease Control",
    description: "Integrated approach to controlling HIV, TB, and Malaria through community-based prevention, testing, and treatment services.",
    color: "primary",
    keyActivities: [
      "Mobile outreach clinics providing HIV testing and TB screening",
      "Community-based ART linkage and adherence counseling",
      "TB contact tracing and treatment referrals",
      "Malaria prevention education and net distribution",
      "Training community health workers on integrated approaches",
      "Indoor residual spraying and vector control"
    ],
    targetGroups: [
      "High-burden communities in Rakai District",
      "Undiagnosed HIV-positive individuals",
      "TB contacts and suspected cases",
      "Households in malaria-endemic areas"
    ],
    expectedOutcomes: [
      "Zero new HIV infections by 2030",
      "Elimination of new TB infections by 2030",
      "Reduced malaria transmission rates",
      "Improved treatment adherence and outcomes"
    ]
  },
    {
      id: 'prevention',
      icon: Shield,
      title: "Prevention Programs",
      description: "Enhance community awareness and shift social norms to prevent SGBV against AGYW through comprehensive education and engagement strategies.",
      color: "primary",
      keyActivities: [
        "Community dialogues and sensitization campaigns",
        "Awareness campaigns on SGBV prevention",
        "Engaging men and boys as allies in prevention",
        "Social norm transformation initiatives",
        "Educational workshops in schools and communities",
        "Media campaigns and advocacy"
      ],
      targetGroups: [
        "Community members and leaders",
        "Men and boys as allies",
        "Religious and cultural leaders",
        "School administrators and teachers"
      ],
      expectedOutcomes: [
        "Increased community awareness on SGBV",
        "Positive attitude change towards SGBV",
        "Reduced tolerance for harmful practices",
        "Enhanced male engagement in prevention"
      ]
    },
    {
      id: 'protection',
      icon: Heart,
      title: "Protection Services",
      description: "Strengthen support systems and referral pathways for AGYW survivors of SGBV through comprehensive care and safe spaces.",
      color: "success",
      keyActivities: [
        "Establishment of safe spaces for survivors",
        "Counseling and psychosocial support services",
        "Legal aid and justice support",
        "Health services including PEP and emergency contraceptives",
        "Referral directory creation and dissemination",
        "24/7 helpline services"
      ],
      targetGroups: [
        "SGBV survivors",
        "At-risk adolescent girls",
        "Families of survivors",
        "Community support networks"
      ],
      expectedOutcomes: [
        "Enhanced access to quality support services",
        "Improved survivor recovery and healing",
        "Strengthened referral pathways",
        "Reduced secondary trauma"
      ]
    },
    {
      id: 'empowerment',
      icon: Users,
      title: "Empowerment Initiatives",
      description: "Equip AGYW with knowledge, skills, and resources to assert their rights and make informed decisions about their lives and futures.",
      color: "warning",
      keyActivities: [
        "Life skills and leadership training",
        "Vocational and technical skills training",
        "Mentorship and role model programs",
        "Peer educator development",
        "Financial literacy and entrepreneurship",
        "Rights awareness and advocacy training"
      ],
      targetGroups: [
        "Adolescent girls (10-19 years)",
        "Young women (20-24 years)",
        "Out-of-school youth",
        "Vulnerable and marginalized girls"
      ],
      expectedOutcomes: [
        "Improved self-efficacy and confidence",
        "Enhanced decision-making capabilities",
        "Increased economic opportunities",
        "Stronger leadership skills"
      ]
    },
    {
      id: 'coordination',
      icon: Award,
      title: "Community Coordination",
      description: "Foster multi-sectoral collaboration among stakeholders to ensure a cohesive and effective SGBV response system.",
      color: "info",
      keyActivities: [
        "Multi-stakeholder coordination meetings",
        "Resource sharing and joint programming",
        "Policy advocacy and reform",
        "Monitoring and evaluation systems",
        "Knowledge sharing platforms",
        "Capacity building for partners"
      ],
      targetGroups: [
        "Government agencies",
        "NGOs and CBOs",
        "Healthcare providers",
        "Educational institutions",
        "Religious organizations"
      ],
      expectedOutcomes: [
        "Strengthened coordination mechanisms",
        "Improved resource utilization",
        "Enhanced policy environment",
        "Sustainable program implementation"
      ]
    }
  ];

const targetBeneficiaries = [
  {
    icon: Users,
    number: "2,000+",
    title: "Community Members",
    description: "Individuals reached through integrated HIV, TB, Malaria services",
    color: "primary"
  },
  {
    icon: Heart,
    number: "200",
    title: "Adolescent Girls & Young Women",
    description: "Aged 10-24 receiving SGBV prevention and empowerment services",
    color: "success"
  },
  {
    icon: BookOpen,
    number: "130+",
    title: "Healthcare Providers",
    description: "Trained in integrated disease control and SGBV response",
    color: "info"
  }
];

  const implementationPhases = [
    {
      phase: "Phase 1",
      duration: "Months 1-6",
      title: "Foundation & Setup",
      activities: [
        "Baseline studies and needs assessment",
        "Stakeholder mapping and engagement",
        "Staff recruitment and training",
        "Community entry and sensitization"
      ]
    },
    {
      phase: "Phase 2", 
      duration: "Months 7-12",
      title: "Program Implementation",
      activities: [
        "Launch of prevention campaigns",
        "Establishment of safe spaces",
        "Training of service providers",
        "Empowerment program rollout"
      ]
    },
    {
      phase: "Phase 3",
      duration: "Months 13-18",
      title: "Consolidation & Evaluation",
      activities: [
        "Program evaluation and assessment",
        "Sustainability planning",
        "Knowledge sharing and dissemination",
        "Transition planning"
      ]
    }
  ];

  return (
    <div className="programs-page">
      {/* Bootstrap CSS */}
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet" 
      />

      {/* Hero Section */}
      <section className="programs-hero">
        <div className="container">
          <div className="row align-items-center min-vh-60">
            <div className="col-lg-8">
              <div className="hero-content">
                <span className="hero-badge">Our Programs</span>
                <h1 className="hero-title">
                  Comprehensive Solutions for Lasting Change
                </h1>
                <p className="hero-subtitle">
                  Our four-pillar approach ensures holistic support for adolescent girls and young women 
                  through evidence-based interventions, community engagement, and sustainable partnerships 
                  to combat Sexual and Gender-Based Violence in Rakai District.
                </p>
                <div className="hero-stats">
                  <div className="stat-item">
                    <span className="stat-number">4</span>
                    <span className="stat-label">Key Programs</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">18</span>
                    <span className="stat-label">Months Duration</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">2,000+</span>
                    <span className="stat-label">Beneficiaries</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="programs-visual">
                <div className="programs-shape-container">
                  <div className="program-shape diamond-shape">
                    <div className="diamond-content">
                      <Shield size={35} />
                      <span className="diamond-text">4</span>
                    </div>
                  </div>
                  
                  <div className="pillar-circle circle-1">
                    <Shield size={18} />
                  </div>
                  <div className="pillar-circle circle-2">
                    <Heart size={18} />
                  </div>
                  <div className="pillar-circle circle-3">
                    <Users size={18} />
                  </div>
                  <div className="pillar-circle circle-4">
                    <Award size={18} />
                  </div>
                  
                  <div className="program-shape hexagon-shape">
                    <div className="hexagon-content">
                      <Target size={24} />
                    </div>
                  </div>
                  
                  <div className="program-shape triangle-shape">
                    <CheckCircle size={20} />
                  </div>
                  
                  <div className="connection-lines">
                    <div className="line line-1"></div>
                    <div className="line line-2"></div>
                    <div className="line line-3"></div>
                    <div className="line line-4"></div>
                  </div>
                  
                  <div className="floating-elements">
                    <div className="float-element element-1">18M</div>
                    <div className="float-element element-2">2K+</div>
                    <div className="float-element element-3">4</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

            {/* Community Impact Gallery */}
      <section className="community-gallery">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Community Impact</span>
            <h2 className="section-title">Stories of Change</h2>
            <p className="section-description">
              Witness the transformative power of our programs through the lives 
              and communities we've touched across Rakai District.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="gallery-card" style={{ animationDelay: '0.1s' }}>
                <div className="image-wrapper">
                  <img src="images/community/comm4.jpg" alt="Community engagement and education programs" className="gallery-image" />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <Eye size={24} />
                      <span>Prevention Programs</span>
                    </div>
                  </div>
                </div>
                <div className="gallery-content">
                  <h4 className="gallery-title">Community Education</h4>
                  <p className="gallery-description">
                    Empowering communities through awareness campaigns and educational workshops 
                    that challenge harmful social norms.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="gallery-card" style={{ animationDelay: '0.2s' }}>
                <div className="image-wrapper">
                  <img src="images/community/comm5.jpg" alt="Safe spaces and support services" className="gallery-image" />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <Heart size={24} />
                      <span>Protection Services</span>
                    </div>
                  </div>
                </div>
                <div className="gallery-content">
                  <h4 className="gallery-title">Safe Spaces</h4>
                  <p className="gallery-description">
                    Creating secure environments where survivors receive comprehensive 
                    support and healing services.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="gallery-card" style={{ animationDelay: '0.3s' }}>
                <div className="image-wrapper">
                  <img src="images/community/comm6.jpg" alt="Youth empowerment and skills training" className="gallery-image" />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <Users size={24} />
                      <span>Training Programs</span>
                    </div>
                  </div>
                </div>
                <div className="gallery-content">
                  <h4 className="gallery-title">Training Programs</h4>
                  <p className="gallery-description">
                   Training health care workers, Educators and Community leaders on SGBV prevention and response.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      {/* <section className="programs-overview">
        <div className="container">
          <div className="row g-4">
            {programs.map((program, index) => (
              <div key={index} className="col-lg-6">
                <div 
                  className="program-card"
                  style={{
                    transform: `translateY(${Math.sin((scrollY + index * 200) * 0.003) * 12}px)`,
                    animationDelay: `${index * 0.15}s`
                  }}
                >
                  <div className="program-header">
                    <div className={`program-icon bg-${program.color}`}>
                      <program.icon size={32} />
                    </div>
                    <div className="program-title-section">
                      <h3 className="program-title">{program.title}</h3>
                      <span className={`program-badge bg-${program.color}-subtle text-${program.color}`}>
                        {program.keyActivities.length} Activities
                      </span>
                    </div>
                  </div>
                  <p className="program-description">{program.description}</p>
                  <button 
                    className={`btn btn-${program.color} btn-sm`}
                    onClick={() => setActiveTab(index)}
                  >
                    View Details <ArrowRight size={16} className="ms-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Program Details Tabs */}
      <section className="program-details">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="program-tabs">
                <div className="tab-navigation">
                  {programs.map((program, index) => (
                    <button
                      key={index}
                      className={`tab-button ${activeTab === index ? 'active' : ''}`}
                      onClick={() => setActiveTab(index)}
                    >
                      <program.icon size={20} />
                      <span>{program.title}</span>
                    </button>
                  ))}
                </div>

                <div className="tab-content">
                  {programs.map((program, index) => (
                    <div
                      key={index}
                      className={`tab-pane ${activeTab === index ? 'active' : ''}`}
                    >
                      <div className="row g-4">
                        <div className="col-lg-4">
                          <div className="detail-card">
                            <h5 className="detail-title">
                              <CheckCircle size={20} className="me-2 text-success" />
                              Key Activities
                            </h5>
                            <ul className="activity-list">
                              {program.keyActivities.map((activity, idx) => (
                                <li key={idx}>{activity}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="col-lg-4">
                          <div className="detail-card">
                            <h5 className="detail-title">
                              <Users size={20} className="me-2 text-primary" />
                              Target Groups
                            </h5>
                            <ul className="target-list">
                              {program.targetGroups.map((group, idx) => (
                                <li key={idx}>{group}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="col-lg-4">
                          <div className="detail-card">
                            <h5 className="detail-title">
                              <Target size={20} className="me-2 text-warning" />
                              Expected Outcomes
                            </h5>
                            <ul className="outcome-list">
                              {program.expectedOutcomes.map((outcome, idx) => (
                                <li key={idx}>{outcome}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Beneficiaries */}
      <section className="beneficiaries-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Target Beneficiaries</span>
            <h2 className="section-title">Who We Serve</h2>
            <p className="section-description">
              Our programs directly target key demographics to ensure maximum impact 
              and sustainable change in the fight against SGBV.
            </p>
          </div>

          <div className="row g-4">
            {targetBeneficiaries.map((beneficiary, index) => (
              <div key={index} className="col-lg-3 col-md-6">
                <div 
                  className="beneficiary-card"
                  style={{
                    transform: `translateY(${Math.sin((scrollY + index * 100) * 0.005) * 8}px)`,
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <div className={`beneficiary-icon bg-${beneficiary.color}`}>
                    <beneficiary.icon size={28} />
                  </div>
                  <h3 className={`beneficiary-number text-${beneficiary.color}`}>
                    {beneficiary.number}
                  </h3>
                  <h4 className="beneficiary-title">{beneficiary.title}</h4>
                  <p className="beneficiary-description">{beneficiary.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Implementation Timeline</span>
            <h2 className="section-title">18-Month Program Journey</h2>
            <p className="section-description">
              A structured approach to program implementation ensuring systematic 
              delivery and sustainable impact across all intervention areas.
            </p>
          </div>

          <div className="timeline-container">
            {implementationPhases.map((phase, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">
                  <span className="phase-number">{index + 1}</span>
                </div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <h4 className="phase-title">{phase.title}</h4>
                    <span className="phase-duration">{phase.duration}</span>
                  </div>
                  <div className="phase-activities">
                    <ul>
                      {phase.activities.map((activity, idx) => (
                        <li key={idx}>
                          <CheckCircle size={16} className="me-2 text-success" />
                          {activity}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        :root {
          --primary-blue: #2563eb;
          --secondary-blue: #1e40af;
          --accent-teal: #06b6d4;
          --accent-orange: #f59e0b;
          --success-green: #10b981;
          --text-gray: #6b7280;
          --light-gray: #f8fafc;
          --info-blue: #3b82f6;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #374151;
          padding-top: 80px;
        }

        /* Hero Section Enhanced Styles */
        .programs-hero {
          padding: 2rem 0;
          background: linear-gradient(135deg, var(--light-gray) 0%, #ffffff 100%);
          position: relative;
          overflow: hidden;
        }

        .min-vh-60 {
          min-height: 60vh;
        }

        .hero-content {
          max-width: 700px;
        }

        .hero-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          display: inline-block;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .hero-stats {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 2rem;
          font-weight: 700;
          color: var(--primary-blue);
          line-height: 1;
        }

        .stat-label {
          font-size: 0.9rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        .programs-visual {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 450px;
        }

        .programs-shape-container {
          position: relative;
          width: 300px;
          height: 300px;
        }

        /* Central Diamond Shape */
        .diamond-shape {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(45deg) translateY(-10px);
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, var(--primary-blue), var(--accent-teal));
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 15px 40px rgba(37, 99, 235, 0.3);
          animation: diamondFloat 6s ease-in-out infinite;
          z-index: 3;
        }

        .diamond-content {
          transform: rotate(-45deg);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          color: white;
        }

        .diamond-text {
          font-size: 1.2rem;
          font-weight: 700;
        }

        /* Pillar Circles */
        .pillar-circle {
          position: absolute;
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          animation: pillarOrbit 8s ease-in-out infinite;
          color: var(--primary-blue);
        }

        .circle-1 {
          top: 30px;
          left: 50%;
          transform: translateX(-50%);
          animation-delay: 0s;
        }

        .circle-2 {
          top: 50%;
          right: 30px;
          transform: translateY(-50%);
          animation-delay: 2s;
          color: var(--success-green);
        }

        .circle-3 {
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          animation-delay: 4s;
          color: var(--accent-orange);
        }

        .circle-4 {
          top: 50%;
          left: 30px;
          transform: translateY(-50%);
          animation-delay: 6s;
          color: #8b5cf6;
        }

        /* Hexagon Shape */
        .hexagon-shape {
          position: absolute;
          top: 70px;
          right: 70px;
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, var(--success-green), #34d399);
          transform: translateY(-8px);
          animation: hexagonSpin 10s linear infinite;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        }

        /* Triangle Shape */
        .triangle-shape {
          position: absolute;
          bottom: 80px;
          left: 80px;
          width: 45px;
          height: 45px;
          background: linear-gradient(135deg, var(--accent-orange), #fbbf24);
          transform: translateY(-5px);
          animation: triangleBounce 4s ease-in-out infinite;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 6px 20px rgba(245, 158, 11, 0.3);
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
        }

        /* Connection Lines */
        .connection-lines {
          position: absolute;
          inset: 0;
        }

        .line {
          position: absolute;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(37, 99, 235, 0.4), transparent);
          animation: linePulse 3s ease-in-out infinite;
        }

        .line-1 {
          top: 90px;
          left: 50%;
          width: 40px;
          transform: translateX(-50%);
          animation-delay: 0s;
        }

        .line-2 {
          top: 50%;
          right: 90px;
          width: 40px;
          transform: translateY(-50%) rotate(90deg);
          animation-delay: 0.75s;
        }

        .line-3 {
          bottom: 90px;
          left: 50%;
          width: 40px;
          transform: translateX(-50%);
          animation-delay: 1.5s;
        }

        .line-4 {
          top: 50%;
          left: 90px;
          width: 40px;
          transform: translateY(-50%) rotate(90deg);
          animation-delay: 2.25s;
        }

        /* Floating Elements */
        .floating-elements {
          position: absolute;
          inset: 0;
        }

        .float-element {
          position: absolute;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 8px;
          padding: 4px 8px;
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--primary-blue);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          animation: floatDrift 5s ease-in-out infinite;
        }

        .element-1 {
          top: 20px;
          right: 20px;
          animation-delay: 1s;
        }

        .element-2 {
          bottom: 20px;
          left: 20px;
          animation-delay: 3s;
        }

        .element-3 {
          top: 60%;
          left: 10px;
          animation-delay: 5s;
        }

        /* Animations */
        @keyframes diamondFloat {
          0%, 100% {
            transform: translate(-50%, -50%) rotate(45deg) translateY(-10px);
          }
          50% {
            transform: translate(-50%, -50%) rotate(45deg) translateY(-25px);
          }
        }

        @keyframes pillarOrbit {
          0%, 100% {
            transform: translateX(-50%) scale(1);
            opacity: 0.9;
          }
          50% {
            transform: translateX(-50%) scale(1.1);
            opacity: 1;
          }
        }

        @keyframes hexagonSpin {
          from {
            transform: translateY(-8px) rotate(0deg);
          }
          to {
            transform: translateY(-8px) rotate(360deg);
          }
        }

        @keyframes triangleBounce {
          0%, 100% {
            transform: translateY(-5px);
          }
          50% {
            transform: translateY(-15px);
          }
        }

        @keyframes linePulse {
          0%, 100% {
            opacity: 0.3;
            transform: scaleX(0.8);
          }
          50% {
            opacity: 0.8;
            transform: scaleX(1.2);
          }
        }

        @keyframes floatDrift {
          0%, 100% {
            transform: translateY(0px);
            opacity: 0.8;
          }
          50% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }

        /* Programs Overview */
        .programs-overview {
          padding: 5rem 0;
        }

        .program-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          height: 100%;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
          position: relative;
        }

        .program-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-blue), var(--accent-teal));
          border-radius: 1.5rem 1.5rem 0 0;
        }

        .program-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 25px 80px rgba(0,0,0,0.15);
        }

        .program-header {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .program-icon {
          width: 70px;
          height: 70px;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .program-title-section {
          flex: 1;
        }

        .program-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 0.5rem;
        }

        .program-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .program-description {
          color: var(--text-gray);
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        /* Program Details Tabs */
        .program-details {
          padding: 5rem 0;
          background: var(--light-gray);
        }

        .program-tabs {
          background: white;
          border-radius: 1.5rem;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .tab-navigation {
          display: flex;
          background: var(--light-gray);
          padding: 1rem;
          gap: 0.5rem;
          overflow-x: auto;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 1.5rem;
          background: transparent;
          border: none;
          border-radius: 0.75rem;
          color: var(--text-gray);
          font-weight: 500;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .tab-button.active {
          background: var(--primary-blue);
          color: white;
        }

        .tab-button:hover {
          background: rgba(37, 99, 235, 0.1);
        }

        .tab-button.active:hover {
          background: var(--secondary-blue);
        }

        .tab-content {
          padding: 3rem;
        }

        .tab-pane {
          display: none;
        }

        .tab-pane.active {
          display: block;
          animation: fadeIn 0.5s ease-out;
        }

        .detail-card {
          background: var(--light-gray);
          border-radius: 1rem;
          padding: 2rem;
          height: 100%;
        }

        .detail-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
        }

        .activity-list,
        .target-list,
        .outcome-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .activity-list li,
        .target-list li,
        .outcome-list li {
          padding: 0.5rem 0;
          color: var(--text-gray);
          border-bottom: 1px solid rgba(0,0,0,0.05);
          position: relative;
          padding-left: 1.5rem;
        }

        .activity-list li::before,
        .target-list li::before,
        .outcome-list li::before {
          content: '•';
          color: var(--primary-blue);
          font-weight: bold;
          position: absolute;
          left: 0;
        }

        .activity-list li:last-child,
        .target-list li:last-child,
        .outcome-list li:last-child {
          border-bottom: none;
        }

        /* Community Gallery */
        .community-gallery {
          padding: 5rem 0;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1rem;
          display: inline-block;
        }

        .section-title {
          font-size: clamp(2.5rem, 4vw, 3.5rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .section-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .gallery-card {
          background: white;
          border-radius: 1.5rem;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.4s ease;
          height: 100%;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
          position: relative;
        }

        .gallery-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-blue), var(--accent-teal));
          z-index: 2;
        }

        .gallery-card::after {
          content: '';
          position: absolute;
          bottom: 2rem;
          left: 2rem;
          width: 40px;
          height: 3px;
          background: linear-gradient(90deg, var(--primary-blue), var(--accent-teal));
          border-radius: 2px;
          z-index: 1;
        }

        .gallery-card:hover {
          transform: translateY(-15px);
          box-shadow: 0 30px 80px rgba(0,0,0,0.2);
        }

        .image-wrapper {
          position: relative;
          height: 280px;
          overflow: hidden;
        }

        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.4s ease;
        }

        .gallery-card:hover .gallery-image {
          transform: scale(1.1);
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.85), rgba(6, 182, 212, 0.85));
          opacity: 0;
          transition: all 0.4s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .gallery-card:hover .image-overlay {
          opacity: 1;
        }

        .overlay-content {
          text-align: center;
          color: white;
          transform: translateY(20px);
          transition: all 0.4s ease;
        }

        .gallery-card:hover .overlay-content {
          transform: translateY(0);
        }

        .overlay-content svg {
          margin-bottom: 0.5rem;
          display: block;
          margin-left: auto;
          margin-right: auto;
        }

        .overlay-content span {
          font-size: 1.1rem;
          font-weight: 600;
          display: block;
        }

        .gallery-content {
          padding: 2rem;
        }

        .gallery-title {
          font-size: 1.3rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
          line-height: 1.3;
        }

        .gallery-description {
          color: var(--text-gray);
          line-height: 1.6;
          font-size: 0.95rem;
        }

        /* Beneficiaries Section */
        .beneficiaries-section {
          padding: 5rem 0;
        }

        .beneficiary-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem 1.5rem;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          height: 100%;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
          position: relative;
        }

        .beneficiary-card::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 50px;
          height: 3px;
          background: linear-gradient(90deg, var(--primary-blue), var(--accent-teal));
          border-radius: 2px;
        }

        .beneficiary-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 25px 80px rgba(0,0,0,0.15);
        }

        .beneficiary-icon {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .beneficiary-number {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          display: block;
        }

        .beneficiary-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .beneficiary-description {
          color: var(--text-gray);
          font-size: 0.95rem;
          line-height: 1.5;
        }

        /* Timeline Section */
        .timeline-section {
          padding: 5rem 0;
          background: var(--light-gray);
        }

        .timeline-container {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
        }

        .timeline-container::before {
          content: '';
          position: absolute;
          left: 30px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--primary-blue);
        }

        .timeline-item {
          position: relative;
          margin-bottom: 3rem;
          padding-left: 80px;
        }

        .timeline-marker {
          position: absolute;
          left: 0;
          top: 0;
          width: 60px;
          height: 60px;
          background: var(--primary-blue);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 1.2rem;
          box-shadow: 0 4px 20px rgba(37, 99, 235, 0.3);
        }

        .timeline-content {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .timeline-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .phase-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin: 0;
        }

        .phase-duration {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .phase-activities ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .phase-activities li {
          display: flex;
          align-items: center;
          padding: 0.5rem 0;
          color: var(--text-gray);
        }

        /* Color Utilities */
        .bg-primary { background-color: var(--primary-blue) !important; }
        .bg-success { background-color: var(--success-green) !important; }
        .bg-warning { background-color: var(--accent-orange) !important; }
        .bg-info { background-color: var(--info-blue) !important; }

        .bg-primary-subtle { background-color: rgba(37, 99, 235, 0.1) !important; }
        .bg-success-subtle { background-color: rgba(16, 185, 129, 0.1) !important; }
        .bg-warning-subtle { background-color: rgba(245, 158, 11, 0.1) !important; }
        .bg-info-subtle { background-color: rgba(59, 130, 246, 0.1) !important; }

        .text-primary { color: var(--primary-blue) !important; }
        .text-success { color: var(--success-green) !important; }
        .text-warning { color: var(--accent-orange) !important; }
        .text-info { color: var(--info-blue) !important; }

        /* Button Styles */
        .btn-primary {
          background-color: var(--primary-blue);
          border-color: var(--primary-blue);
        }

        .btn-primary:hover {
          background-color: var(--secondary-blue);
          border-color: var(--secondary-blue);
        }

        .btn-success {
          background-color: var(--success-green);
          border-color: var(--success-green);
        }

        .btn-success:hover {
          background-color: #059669;
          border-color: #059669;
        }

        .btn-warning {
          background-color: var(--accent-orange);
          border-color: var(--accent-orange);
          color: white;
        }

        .btn-warning:hover {
          background-color: #d97706;
          border-color: #d97706;
          color: white;
        }

        .btn-info {
          background-color: var(--info-blue);
          border-color: var(--info-blue);
        }

        .btn-info:hover {
          background-color: #2563eb;
          border-color: #2563eb;
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .programs-visual {
            display: none;
          }
        }

        @media (max-width: 768px) {
          body {
            padding-top: 70px;
          }
          
          .hero-stats {
            gap: 1rem;
          }
          
          .stat-item {
            flex: 1;
            min-width: 100px;
          }
          
          .tab-navigation {
            flex-direction: column;
          }
          
          .tab-button {
            justify-content: center;
          }

          .gallery-card {
            margin-bottom: 2rem;
          }

          .image-wrapper {
            height: 250px;
          }

          .gallery-content {
            padding: 1.5rem;
          }
          
          .timeline-container::before {
            left: 20px;
          }
          
          .timeline-item {
            padding-left: 60px;
          }
          
          .timeline-marker {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }
          
          .timeline-header {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        @media (max-width: 576px) {
          .program-header {
            flex-direction: column;
            text-align: center;
          }
          
          .tab-content {
            padding: 2rem 1rem;
          }
          
          .detail-card {
            padding: 1.5rem;
          }

          .image-wrapper {
            height: 220px;
          }

          .gallery-content {
            padding: 1.25rem;
          }

          .gallery-title {
            font-size: 1.2rem;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          .diamond-shape,
          .pillar-circle,
          .hexagon-shape,
          .triangle-shape,
          .line,
          .float-element {
            animation: none;
          }
        }

        /* Enhanced Hover Effects */
        .programs-shape-container:hover .diamond-shape {
          transform: translate(-50%, -50%) rotate(45deg) translateY(-20px);
          box-shadow: 0 20px 60px rgba(37, 99, 235, 0.4);
        }

        .programs-shape-container:hover .pillar-circle {
          animation-duration: 4s;
        }

        .programs-shape-container:hover .line {
          animation-duration: 1.5s;
        }

        /* Glow Effects */
        .diamond-shape::before {
          content: '';
          position: absolute;
          inset: -8px;
          background: linear-gradient(135deg, var(--primary-blue), var(--accent-teal));
          border-radius: inherit;
          z-index: -1;
          opacity: 0.3;
          animation: glowPulse 4s ease-in-out infinite;
        }

        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }

        /* Focus States for Accessibility */
        .tab-button:focus,
        .btn:focus {
          outline: 2px solid var(--accent-orange);
          outline-offset: 2px;
        }

        /* Loading Animation for Cards */
        .program-card,
        .beneficiary-card,
        .gallery-card {
          animation-fill-mode: both;
        }

        /* Smooth Scrolling */
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      {/* Bootstrap JS */}
      <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
    </div>
  );
};

export default ProgramsPage;